import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "../../styles/pages/games/USHistoryQuiz.module.scss";

const LETTERS = ["A","B","C","D"];

// ---------------- Question Bank (add more anytime) ----------------
const BANK = [
  { q: "Which document announced the colonies’ independence from Britain?",
    a: ["U.S. Constitution","Declaration of Independence","Bill of Rights","Articles of Confederation"],
    correct: 1, explain: "Adopted July 4, 1776; primarily authored by Thomas Jefferson." },
  { q: "America’s first national framework of government (1777–1789) was the…",
    a: ["Articles of Confederation","Federalist Papers","Northwest Ordinance","Confederate Constitution"],
    correct: 0, explain: "Weak central powers; replaced by the Constitution in 1789." },
  { q: "The Constitutional Convention met in which city in 1787?",
    a: ["Boston","New York","Philadelphia","Richmond"],
    correct: 2, explain: "Independence Hall, Philadelphia." },
  { q: "Marbury v. Madison (1803) established which principle?",
    a: ["Federal supremacy","Judicial review","States’ rights","Nullification"],
    correct: 1, explain: "Courts can declare laws unconstitutional." },
  { q: "Which event led directly to the Louisiana Purchase exploration?",
    a: ["XYZ Affair","Lewis & Clark Expedition","War of 1812","Missouri Compromise"],
    correct: 1, explain: "Commissioned by Jefferson to explore the 1803 purchase." },
  { q: "The Missouri Compromise (1820) primarily addressed…",
    a: ["Tariff policy","Slavery in new states","Bank charter","Indian removal"],
    correct: 1, explain: "Admitted Missouri (slave) and Maine (free); set 36°30′ line." },
  { q: "Which doctrine warned Europe against further colonization in the Americas?",
    a: ["Roosevelt Corollary","Open Door Policy","Monroe Doctrine","Truman Doctrine"],
    correct: 2, explain: "1823 policy asserting Western Hemisphere off-limits to new colonization." },
  { q: "Which amendment abolished slavery?",
    a: ["13th","14th","15th","16th"],
    correct: 0, explain: "13th Amendment (1865)." },
  { q: "Reconstruction amendments include the 13th, 14th, and 15th. What did the 14th do?",
    a: ["Income tax","Citizenship & equal protection","Direct election of Senators","Women’s suffrage"],
    correct: 1, explain: "Citizenship, due process, and equal protection (1868)." },
  { q: "Which Supreme Court case overturned “separate but equal”?",
    a: ["Plessy v. Ferguson","Brown v. Board of Education","Korematsu v. U.S.","Dred Scott v. Sandford"],
    correct: 1, explain: "Brown (1954) ended legal school segregation." },
  { q: "The New Deal was a response to…",
    a: ["World War I","Great Depression","Cold War","Dust Bowl only"],
    correct: 1, explain: "FDR’s programs addressing the Great Depression." },
  { q: "Which act outlaws discrimination based on race, color, religion, sex, or national origin?",
    a: ["Civil Rights Act 1964","Voting Rights Act 1965","Fair Housing Act 1968","Equal Pay Act 1963"],
    correct: 0, explain: "Title II & VII are key provisions." },
  { q: "The Voting Rights Act (1965) targeted which practice?",
    a: ["Prohibition","Poll taxes only","Literacy tests and barriers to voting","Draft registration"],
    correct: 2, explain: "Suspended literacy tests and enabled federal oversight." },
  { q: "Which amendment gave women the right to vote nationwide?",
    a: ["15th","17th","19th","26th"],
    correct: 2, explain: "19th Amendment (1920)." },
  { q: "What scandal led to President Nixon’s resignation?",
    a: ["Teapot Dome","Iran-Contra","Whitewater","Watergate"],
    correct: 3, explain: "Nixon resigned in 1974 amid Watergate." },
  { q: "The War Powers Resolution (1973) aimed to…",
    a: ["Expand presidential war powers","Limit presidential military action without Congress","Create NATO","Authorize Gulf War"],
    correct: 1, explain: "Requires notification and limits deployments without authorization." },
  { q: "McCulloch v. Maryland (1819) affirmed…",
    a: ["State power to tax federal entities","Narrow reading of Necessary and Proper","Federal supremacy & implied powers","Judicial term limits"],
    correct: 2, explain: "Broad implied powers; states can’t tax the federal bank." },
  { q: "The Sherman Antitrust Act (1890) targeted…",
    a: ["Child labor","Bank failures","Monopolies & restraints of trade","Tariff evasion"],
    correct: 2, explain: "First federal antitrust law." },
  { q: "The Great Society initiatives (1960s) were associated with…",
    a: ["Dwight Eisenhower","Lyndon B. Johnson","Harry Truman","Richard Nixon"],
    correct: 1, explain: "Programs on civil rights, poverty, education, Medicare/Medicaid." },
  { q: "Which amendment lowered the voting age to 18?",
    a: ["21st","22nd","25th","26th"],
    correct: 3, explain: "26th Amendment (1971)." },
  { q: "The Bill of Rights refers to…",
    a: ["First 10 amendments","First 5 articles","Federalist Papers","Colonial charters"],
    correct: 0, explain: "Ratified 1791; core civil liberties." },
  { q: "“Judicial review” allows courts to…",
    a: ["Rewrite statutes","Veto treaties","Strike down unconstitutional laws","Impeach judges"],
    correct: 2, explain: "Established in Marbury v. Madison (1803)." },
  { q: "The Emancipation Proclamation (1863)…",
    a: ["Abolished slavery nationwide","Freed slaves in rebelling states","Freed slaves in border states","Created the 13th Amendment"],
    correct: 1, explain: "Applied to Confederate-held areas; 13th later abolished slavery nationwide." },
  { q: "Which act created the merit-based federal civil service after patronage abuses?",
    a: ["Hatch Act","Pendleton Act","Taft-Hartley Act","Glass-Steagall Act"],
    correct: 1, explain: "Pendleton Civil Service Reform Act (1883)." },
  { q: "Which law expanded health insurance coverage in 2010?",
    a: ["Medicare Act","Affordable Care Act","Social Security Act","HIPAA"],
    correct: 1, explain: "ACA introduced marketplaces, Medicaid expansion (state-dependent), etc." },
];

// --------------- Helpers ---------------
function sampleN(arr, n) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a.slice(0, n);
}

// Sounds + mini-confetti (no deps)
function useSfx() {
  const ref = useRef({ ctx: null, g: null, on: true });
  function setup() {
    if (ref.current.ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    const ctx = new AC();
    const g = ctx.createGain(); g.gain.value = 0.12; g.connect(ctx.destination);
    ref.current.ctx = ctx; ref.current.g = g;
  }
  function blip(f = 600, d = 0.06, type = "triangle", vol = 1) {
    if (!ref.current.on) return;
    if (!ref.current.ctx) setup();
    const o = ref.current.ctx.createOscillator();
    const gg = ref.current.ctx.createGain();
    o.type = type; o.frequency.value = f;
    o.connect(gg); gg.connect(ref.current.g);
    const t = ref.current.ctx.currentTime;
    gg.gain.value = 0.001;
    gg.gain.linearRampToValueAtTime(0.12 * vol, t + 0.01);
    gg.gain.exponentialRampToValueAtTime(0.001, t + d);
    o.start(t); o.stop(t + d + 0.02);
  }
  return {
    correct: () => { blip(700, .06, "triangle", 1); setTimeout(()=>blip(900,.06,"triangle",1), 80); },
    wrong:   () => blip(220, .12, "square", 1),
    end:     () => [660,880,990,1320].forEach((f,i)=> setTimeout(()=>blip(f,.09,"sawtooth",1), i*90)),
    toggle:  () => { ref.current.on = !ref.current.on; blip(720,.05,"triangle"); },
    isOn:    () => ref.current.on
  };
}
function useConfetti() {
  const canvasRef = useRef(null);
  const P = useRef([]); const raf = useRef(0);
  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    const loop = () => {
      const dpr = window.devicePixelRatio || 1;
      if (c.width !== c.clientWidth * dpr || c.height !== c.clientHeight * dpr) {
        c.width = c.clientWidth * dpr; c.height = c.clientHeight * dpr;
      }
      ctx.clearRect(0,0,c.width,c.height);
      for (let i = P.current.length - 1; i >= 0; i--) {
        const p = P.current[i];
        p.x += p.vx; p.y += p.vy; p.vy += 0.03; p.life -= 16;
        if (p.life <= 0) { P.current.splice(i,1); continue; }
        ctx.globalAlpha = Math.max(0, Math.min(1, p.life/600));
        ctx.fillStyle = `hsl(${p.h},90%,60%)`; ctx.fillRect(p.x, p.y, p.r, p.r);
      }
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf.current);
  }, []);
  function burst(n=140){
    const c = canvasRef.current; if(!c) return;
    const dpr = window.devicePixelRatio||1; c.width=c.clientWidth*dpr; c.height=c.clientHeight*dpr;
    for(let i=0;i<n;i++){ const a=Math.random()*Math.PI*2, sp=2+Math.random()*4;
      P.current.push({ x:c.width/2, y:c.height*0.2, vx:Math.cos(a)*sp, vy:Math.sin(a)*sp-2, r:2+Math.random()*3, h:Math.random()*360, life:900+Math.random()*1200 });
    }
  }
  return { canvasRef, burst };
}

// ---------------- Component ----------------
export default function USHistoryQuiz() {
  const sfx = useSfx();
  const confetti = useConfetti();

  const [qs, setQs] = useState(() => sampleN(BANK, 10));
  const [i, setI] = useState(0);
  const [choice, setChoice] = useState(null);
  const [revealed, setRevealed] = useState(false);

  const [score, setScore] = useState(0);
  const [best, setBest] = useState(Number(localStorage.getItem("ush_best") || 0));
  const [done, setDone] = useState(false);

  const current = qs[i];

  function pickAnswer(idx) {
    if (revealed || done) return;
    setChoice(idx);
    setRevealed(true);
    if (idx === current.correct) {
      setScore(s => s + 100);
      sfx.correct();
    } else {
      sfx.wrong();
    }
  }

  function next() {
    if (!revealed) return;
    if (i + 1 >= qs.length) {
      setDone(true);
      // end + save best + confetti for strong finishes
      setBest(b => {
        const next = Math.max(b, score);
        localStorage.setItem("ush_best", String(next));
        return next;
      });
      if (score >= 800) confetti.burst(160);
      sfx.end();
      return;
    }
    setI(x => x + 1);
    setChoice(null);
    setRevealed(false);
  }

  function restart() {
    setQs(sampleN(BANK, 10));
    setI(0); setChoice(null); setRevealed(false);
    setScore(0); setDone(false);
  }

  // keyboard shortcuts
  useEffect(() => {
    function onKey(e) {
      if (done) return;
      if (!revealed) {
        if (["1","2","3","4"].includes(e.key)) pickAnswer(Number(e.key) - 1);
      } else if (e.key === "Enter") {
        next();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [revealed, done, current]); // eslint-disable-line

  const progress = Math.round((i / qs.length) * 100);

  return (
    <div className={styles.wrap}>
      <header className={styles.top}>
        <div className={styles.title}>U.S. Political History</div>
        <div className={styles.stats}>
          <span className={styles.badge}>Score: {score}</span>
          <span className={styles.badge}>Best: {best}</span>
          <button className={styles.btn} onClick={sfx.toggle}>{sfx.isOn() ? "🔊" : "🔇"}</button>
          <button className={styles.btn} onClick={restart}>Shuffle 10</button>
        </div>
      </header>

      <main className={styles.stage}>
        <div className={styles.fxLayer}><canvas ref={confetti.canvasRef} className={styles.fxCanvas} /></div>

        <div className={styles.progress}><div className={styles.progressFill} style={{ width: `${progress}%` }} /></div>

        <section className={styles.card}>
          <div className={styles.qHeader}>
            <div className={styles.qIndex}>Q{i + 1} / {qs.length}</div>
          </div>

          <h3 className={styles.question}>{current.q}</h3>

          <div className={styles.options}>
  {current.a.map((opt, idx) => {
    const isCorrect = idx === current.correct;
    const isChosen = choice === idx;
    const className = [
      styles.opt,
      revealed && isCorrect ? styles.correct : "",
      revealed && isChosen && !isCorrect ? styles.wrong : ""
    ].join(" ");

    return (
      <button
        key={idx}
        className={className}
        onClick={() => pickAnswer(idx)}
        disabled={revealed}
        aria-label={`Answer ${idx + 1}`}
      >
        <span className={styles.letter}>{LETTERS[idx]}</span>
        <span className={styles.optText}>{opt}</span>
      </button>
    );
  })}
</div>

          {revealed && (
            <div className={styles.explain}>
              {current.explain}
            </div>
          )}

          <div className={styles.footer}>
            {!revealed ? (
              <div className={styles.tip}>Press <b>1–4</b> to answer</div>
            ) : (
              <button className={styles.next} onClick={next}>Next ▷ (Enter)</button>
            )}
          </div>
        </section>

        {done && (
          <div className={styles.result}>
            <h3 className={styles.resultTitle}>{score >= 800 ? "Excellent! 🎉" : score >= 600 ? "Great Work!" : "Keep Learning"}</h3>
            <p className={styles.resultSub}>
              Final score: <b>{score}</b> • Best: <b>{Math.max(best, score)}</b>
            </p>
            <div className={styles.resultActions}>
              <button onClick={restart}>Play again</button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
